import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { decodeVinFn, type DecodeVinResult } from "@/lib/vin.functions";
import { faqLd, breadcrumbLd, SITE_OG_IMAGE } from "@/lib/seo";
import { Card, CardContent } from "@/components/ui/card";
import {
  validateVin,
  sectionForPosition,
  SECTION_CLASS,
  SECTION_LABEL,
  POSITION_GUIDE,
  COUNTRY_BY_FIRST_CHAR,
  COMMON_WMI,
  SAMPLE_VINS,
  modelYearFromVin,
  type VinSection,
} from "@/lib/vin-structure";

const PATH = "https://autospecdata.com/vin-decoder";

const FAQS = [
  {
    q: "What is a VIN?",
    a: "A VIN (Vehicle Identification Number) is a unique 17-character code assigned to every road vehicle. It encodes the manufacturer, model, model year, plant of manufacture, and a serial number.",
  },
  {
    q: "Is this VIN decoder free?",
    a: "Yes. It uses NHTSA's public vPIC database and returns results instantly with no sign-up, no limits, and no data stored.",
  },
  {
    q: "Where do I find my VIN?",
    a: "Your VIN is on the driver-side dashboard (visible through the windshield), the driver-side door jamb sticker, your title, registration, and insurance card.",
  },
  {
    q: "Why do some fields come back empty?",
    a: "NHTSA's decoder relies on data submitted by manufacturers. Older vehicles, aftermarket conversions, and some trims may have incomplete records for certain fields like horsepower or trim.",
  },
  {
    q: "What is the check digit in a VIN?",
    a: "Position 9 is a checksum calculated from the other 16 characters using the formula in 49 CFR Part 565. Our decoder verifies it as you type, so a mistyped or fabricated VIN is flagged before you even search.",
  },
  {
    q: "Does a VIN decode show accident or title history?",
    a: "No. Decoding a VIN reveals how the vehicle was built at the factory — year, make, model, trim, engine, plant and original safety equipment. Ownership, accident, salvage-title and odometer records come from paid history reports and are not part of the VIN itself.",
  },
  {
    q: "Can I decode a partial VIN?",
    a: "The first 11 characters already identify the manufacturer, body style, engine family, model year and plant. Our character map explains those positions even without the full 17, but a full VIN is required for the official NHTSA decode.",
  },
];

export const Route = createFileRoute("/vin-decoder")({
  component: VinDecoderPage,
  head: () => ({
    meta: [
      { title: "Free VIN Decoder — Decode Any VIN | AutoSpecData" },
      {
        name: "description",
        content:
          "Decode any 17-character VIN free: year, make, model, trim, engine, transmission, drivetrain and plant — from NHTSA's official vPIC database.",
      },
      { property: "og:title", content: "Free VIN Decoder | AutoSpecData" },
      {
        property: "og:description",
        content:
          "Decode any vehicle's 17-character VIN instantly — year, make, model, trim, engine, transmission, and more from NHTSA's official database.",
      },
      { property: "og:url", content: PATH },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: SITE_OG_IMAGE },
      { name: "twitter:image", content: SITE_OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: PATH }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Free VIN Decoder",
          url: PATH,
          description:
            "Decode any 17-character VIN for free — year, make, model, trim, engine, transmission, drivetrain, and safety equipment.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqLd(FAQS)),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "VIN Decoder", path: PATH },
          ]),
        ),
      },
    ],
  }),
});

interface FieldRow {
  label: string;
  value: string | null | undefined;
}

function Group({ title, fields }: { title: string; fields: FieldRow[] }) {
  const visible = fields.filter((f) => f.value);
  if (visible.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-4">
        <h2 className="font-heading font-semibold text-foreground mb-3">{title}</h2>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {visible.map((f) => (
            <div key={f.label} className="contents">
              <dt className="text-muted-foreground">{f.label}</dt>
              <dd className="text-foreground font-medium">{f.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

function VinDecoderPage() {
  const decode = useServerFn(decodeVinFn);
  const [vin, setVin] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [result, setResult] = useState<DecodeVinResult | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [copied, setCopied] = useState(false);

  const check = validateVin(vin);
  const guessedYear = vin.length === 17 ? modelYearFromVin(vin) : null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setState("loading");
    setErrorMsg("");
    try {
      const res = await decode({ data: { vin: vin.trim() } });
      setResult(res);
      if (!res.ok) {
        setState("error");
        setErrorMsg(res.error ?? "That VIN could not be decoded.");
      } else {
        setState("done");
      }
    } catch (err) {
      setState("error");
      setErrorMsg(
        err instanceof Error && err.message
          ? err.message
          : "That VIN could not be decoded. Please check it and try again.",
      );
    }
  }

  const d = result?.ok ? result.decoded : null;

  const usedSections = Array.from(
    new Set(POSITION_GUIDE.map((p) => p.section)),
  ) as VinSection[];

  return (
    <div>
      <nav className="mx-auto max-w-4xl px-4 sm:px-6 pt-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">VIN Decoder</span>
      </nav>

      <section className="border-b border-border section-band">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
          <p className="eyebrow">Free tool · NHTSA vPIC data</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-foreground">
            Free VIN Decoder — decode any 17-digit VIN
          </h1>
          <p data-quick-answer className="mt-4 max-w-3xl text-base sm:text-lg leading-relaxed text-foreground">
            Enter any 17-character Vehicle Identification Number to instantly see its year,
            make, model, trim, engine, transmission, drivetrain, assembly plant and safety
            equipment — decoded live from NHTSA's official vPIC database, free with no sign-up.
          </p>

          {/* ---------- Decoder card ---------- */}
          <div className="card-premium mt-6 p-5 sm:p-6">
            <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                required
                maxLength={17}
                value={vin}
                onChange={(e) => setVin(e.target.value.toUpperCase().replace(/\s/g, ""))}
                placeholder="1HGCM82633A004352"
                aria-label="Vehicle Identification Number"
                spellCheck={false}
                autoComplete="off"
                className="w-full flex-1 rounded-lg border border-border bg-background px-4 py-3 font-mono text-base tracking-[0.18em] text-foreground uppercase outline-none focus:border-primary"
              />
              <button
                type="submit"
                disabled={state === "loading" || vin.length !== 17}
                className="cta-gold px-6 py-3"
              >
                {state === "loading" ? "Decoding…" : "Decode VIN"}
              </button>
            </form>

            {/* Live character map — shows which part of the VIN means what. */}
            {vin.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <div className="flex gap-1">
                  {Array.from({ length: 17 }).map((_, i) => {
                    const section = sectionForPosition(i + 1);
                    const ch = vin[i];
                    return (
                      <div key={i} className="flex w-7 shrink-0 flex-col items-center gap-1">
                        <span
                          title={SECTION_LABEL[section]}
                          className={`flex h-8 w-7 items-center justify-center rounded border font-mono text-sm font-semibold ${
                            ch ? SECTION_CLASS[section] : "border-dashed border-border text-muted-foreground"
                          }`}
                        >
                          {ch ?? "·"}
                        </span>
                        <span className="text-[10px] text-muted-foreground">{i + 1}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {usedSections.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1.5">
                      <span className={`h-2.5 w-2.5 rounded-sm border ${SECTION_CLASS[s]}`} />
                      {SECTION_LABEL[s]}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {check.status !== "empty" && (
              <p
                className={`mt-3 text-sm ${
                  check.status === "valid"
                    ? "text-success"
                    : check.status === "short"
                      ? "text-muted-foreground"
                      : "text-destructive"
                }`}
              >
                {check.message}
                {check.status === "valid" && guessedYear
                  ? ` Model-year code suggests ${guessedYear}.`
                  : ""}
              </p>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-muted-foreground">Try a sample:</span>
              {SAMPLE_VINS.map((s) => (
                <button
                  key={s.vin}
                  type="button"
                  onClick={() => {
                    setVin(s.vin);
                    setState("idle");
                    setResult(null);
                  }}
                  className="rounded-full border border-border bg-secondary px-3 py-1 font-medium text-secondary-foreground hover:border-primary"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
        {state === "error" && (
          <p role="alert" className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            {errorMsg}
          </p>
        )}

        {state === "done" && d && (
          <div className="space-y-6">
            <div className="card-premium flex flex-wrap items-start justify-between gap-4 p-5">
              <div>
                <div className="eyebrow">Decoded VIN</div>
                <div className="mt-1 font-mono text-lg font-semibold tracking-[0.14em] text-foreground">
                  {d.vin}
                </div>
                <div className="mt-1 font-heading text-2xl font-bold text-foreground">
                  {[d.year, d.make, d.model, d.trim].filter(Boolean).join(" ")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(d.vin);
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1500);
                }}
                className="rounded-lg border border-border px-3 py-2 text-xs font-semibold text-foreground hover:border-primary"
              >
                {copied ? "Copied" : "Copy VIN"}
              </button>
            </div>

            {result?.specLink && (
              <div className="rounded-lg border border-primary/40 bg-secondary/40 p-4">
                <p className="text-sm text-foreground">
                  We have full verified specs for the{" "}
                  <span className="font-semibold">
                    {result.specLink.makeName} {result.specLink.modelName}
                  </span>{" "}
                  on AutoSpecData.
                </p>
                <div className="mt-2 flex flex-wrap gap-3 text-sm">
                  <Link
                    to="/$make/$model"
                    params={{ make: result.specLink.makeSlug, model: result.specLink.modelSlug }}
                    className="text-primary underline font-medium"
                  >
                    View {result.specLink.makeName} {result.specLink.modelName} specs
                  </Link>
                  <Link
                    to="/$make/$model/$dataType"
                    params={{
                      make: result.specLink.makeSlug,
                      model: result.specLink.modelSlug,
                      dataType: "mpg",
                    }}
                    className="text-primary underline font-medium"
                  >
                    Fuel economy
                  </Link>
                  <Link
                    to="/$make/$model/$dataType"
                    params={{
                      make: result.specLink.makeSlug,
                      model: result.specLink.modelSlug,
                      dataType: "engine",
                    }}
                    className="text-primary underline font-medium"
                  >
                    Engine specs
                  </Link>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <Group
                title="Vehicle"
                fields={[
                  { label: "Model Year", value: d.year },
                  { label: "Make", value: d.make },
                  { label: "Model", value: d.model },
                  { label: "Trim", value: d.trim },
                  { label: "Series", value: d.series },
                  { label: "Body Class", value: d.bodyClass },
                  { label: "Vehicle Type", value: d.vehicleType },
                  { label: "Doors", value: d.doors },
                  { label: "Manufacturer", value: d.manufacturer },
                ]}
              />
              <Group
                title="Engine & Drivetrain"
                fields={[
                  { label: "Cylinders", value: d.engineCylinders },
                  { label: "Displacement (L)", value: d.engineDisplacementL },
                  { label: "Horsepower", value: d.engineHp ? `${d.engineHp} hp` : null },
                  { label: "Engine Model", value: d.engineModel },
                  { label: "Fuel Type", value: d.fuelType },
                  { label: "Secondary Fuel", value: d.fuelTypeSecondary },
                  { label: "Drive Type", value: d.driveType },
                  { label: "Transmission", value: d.transmissionStyle },
                  { label: "Transmission Speeds", value: d.transmissionSpeeds },
                ]}
              />
              <Group
                title="Manufacturing"
                fields={[
                  { label: "Plant Country", value: d.plantCountry },
                  { label: "Plant State", value: d.plantState },
                  { label: "Plant City", value: d.plantCity },
                  { label: "GVWR", value: d.gvwr },
                ]}
              />
              <Group
                title="Safety Equipment"
                fields={[
                  { label: "ABS", value: d.abs },
                  { label: "Electronic Stability Control", value: d.esc },
                  { label: "Front Air Bag Location", value: d.airBagLocations },
                ]}
              />
            </div>
          </div>
        )}

        {/* ---------- What a VIN contains ---------- */}
        <div className="mt-12">
          <h2 className="font-heading text-2xl font-bold text-foreground">
            What each position in a VIN means
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Under ISO 3779 and 49 CFR Part 565, a 17-character VIN is a strict blueprint, not a
            random serial. Every character below is assigned a job by the manufacturer.
          </p>
          <div className="mt-4 overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-secondary-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Position</th>
                  <th className="px-4 py-2 text-left font-semibold">Section</th>
                  <th className="px-4 py-2 text-left font-semibold">What it encodes</th>
                </tr>
              </thead>
              <tbody>
                {POSITION_GUIDE.map((row) => (
                  <tr key={row.positions} className="border-t border-border align-top">
                    <td className="px-4 py-3 font-mono font-semibold text-foreground">{row.positions}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded border px-2 py-0.5 text-xs font-medium ${SECTION_CLASS[row.section]}`}>
                        {SECTION_LABEL[row.section]}
                      </span>
                    </td>
                    <td className="px-4 py-3 leading-relaxed text-muted-foreground">{row.what}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ---------- WMI & country reference ---------- */}
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground">
              VIN country codes (first character)
            </h2>
            <div className="mt-3 overflow-hidden rounded-xl border border-border">
              <table className="w-full text-sm">
                <tbody>
                  {COUNTRY_BY_FIRST_CHAR.map((c) => (
                    <tr key={c.code} className="border-b border-border last:border-0">
                      <td className="w-24 px-4 py-2 font-mono font-semibold text-foreground">{c.code}</td>
                      <td className="px-4 py-2 text-muted-foreground">{c.country}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground">
              Common WMI codes by brand
            </h2>
            <div className="mt-3 overflow-hidden rounded-xl border border-border">
              <table className="w-full text-sm">
                <tbody>
                  {COMMON_WMI.map((w) => (
                    <tr key={w.wmi} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 font-mono font-semibold text-foreground">{w.wmi}</td>
                      <td className="px-4 py-2 font-medium text-foreground">{w.brand}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{w.country}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ---------- Decode vs history report ---------- */}
        <div className="mt-12">
          <h2 className="font-heading text-2xl font-bold text-foreground">
            What a free VIN decode does and doesn't show
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="card-premium p-5">
              <h3 className="font-heading font-semibold text-foreground">Included here, free</h3>
              <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                {[
                  "Model year, make, model, trim and series",
                  "Body class, doors and vehicle type",
                  "Engine cylinders, displacement and horsepower",
                  "Transmission style, speeds and drive type",
                  "GVWR and assembly plant city / country",
                  "Factory ABS, stability control and airbag locations",
                ].map((i) => (
                  <li key={i}>✓ {i}</li>
                ))}
              </ul>
            </div>
            <div className="card-premium p-5">
              <h3 className="font-heading font-semibold text-foreground">Not in the VIN itself</h3>
              <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                {[
                  "Accident and collision history",
                  "Salvage, junk or rebuilt title brands",
                  "Odometer readings and rollback checks",
                  "Ownership, lien and theft records",
                ].map((i) => (
                  <li key={i}>✗ {i}</li>
                ))}
              </ul>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                Those come from title and insurance databases, not the number stamped on the car.
                A decode tells you how the vehicle was built — a paid history report tells you what
                happened to it afterwards.
              </p>
            </div>
          </div>
        </div>

        <div className="prose prose-sm max-w-none mt-12">
          <h2 className="text-xl font-bold text-foreground mb-3">Frequently Asked Questions</h2>
          {FAQS.map((f) => (
            <div key={f.q} className="mb-4">
              <h3 className="font-semibold text-foreground">{f.q}</h3>
              <p className="text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-10 text-xl font-bold text-foreground">Explore more</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Want deeper, verified specs by model year instead of a single VIN? Browse{" "}
          <Link to="/best" className="text-primary underline">
            fuel economy rankings
          </Link>
          , use our{" "}
          <Link to="/compare" className="text-primary underline">
            vehicle comparison tool
          </Link>
          , or try the{" "}
          <Link
            to="/tools/$slug"
            params={{ slug: "mpg-calculator" }}
            className="text-primary underline"
          >
            MPG calculator
          </Link>
          .
        </p>
      </section>
    </div>
  );
}
